// @flow

import React, {Component} from 'react';
import Button from '@react/react-spectrum/Button';
import ModalTrigger from '@react/react-spectrum/ModalTrigger';
import AppCard from '@ccxi/ccxi-shared-components/src/AppCard';
import CardSkeleton from 'cchome-shared/skeleton/CardSkeleton';
import IntentDropdown from './../IntentDropdown/IntentDropdown';
import MessageBox from '../MessageBox/MessageBox';
import PlanModalContent from '../PlanModalContent/PlanModalContent';
import PlanModalContentScss from '../PlanModalContent/PlanModalContent.scss';
import Dialog from '@react/react-spectrum/Dialog';
import AppPickerUtil from '../../../../utils/AppPicker/AppPickerUtil';
import HelpPopoverUtil from '../../../../utils/AppPicker/HelpPopoverUtil';
import {
	ACTION_IDS,
	HELP_TIMEOUT,
	AUTO_DOWNLOAD_TIMEOUT,
	ALL_APPS,
	GET_HELP_STRINGS,
	LIST_IDS,
	DOWNLOAD_VIA_CCD_QUERY_PARAM,
	TAGS,
	MESSAGE_BOX_VARIANT,
	GET_HELP_OPEN_SOURCES,
	INSTALL_CCD_OS_TOAST,
	DOWNLOAD_CONTROLLER_USER_SOURCE
} from '../../../../constants/AppPicker/appPickerConstants';
import AppsUtil from '../../../../utils/AppsUtil';
import CompatibilityUtil from '../../../../utils/CompatibilityUtil';
import CSSUtil from '../../../../utils/CSSUtil';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import HomeViewScss from './HomeView.scss';
import IncompatibleDialogUtil from '../../../../utils/IncompatibleDialogUtil';
import uuid from 'uuid';

const ANALYTICS = {
	PAGE_RENDER: 'page_render',
	APP_RENDER: 'app_render',
	APP_CARD_CLICK: 'app_card_click',
	APP_CTA_CLICK: 'app_cta_click',
	PAGE_CTA_CLICK: 'page_cta_click',
	PAGE_FILTER_CLICK: 'page_filter_click',
};

type Props = {
	selectedCategoryId: string,
	selectedTaskId: string,
	locale: string,
	environment: string,
	onCategorySelect: Function,
	onTaskSelect: Function,
	showIncompatibilityDialog: Function,
	onIncompatibleOSToastDismiss: Function,
	clearHelpTimeouts: Function,
	showIncompatibleOSToast: boolean,
	enableHelpTimeouts: boolean,
	renderOnlyCategories: boolean,
	hasThor: ?boolean,
	imsData: Object,
	history: Object,
	intl: Object,
	sapCodeDataFetchStatus: Array<Object>
};

type AnalyticObjectInterface = {
	event: {
		type?: string,
		subtype?: ?string,
		subcategory?: string,
		value?: string,
		guid?: ?string
	},
	ui?: {
		filter?: ?string,
		view_type?: ?string
	},
	content?: {
		id?: ?string,
		name?: ?string,
		type?: string,
		version?: ?string,
		category?: string,
		action?: ?string
	}
};

type AnalyticsParam = {
	sapCode?: string,
	appName?: string,
	actionId?: string,
	ctaType?: string,
	guid?: string,
	url?: string,
	filterValue?: string
};

type AppObject = {
	sapCode: string,
	appName: string,
	platform: string
}

export default class HomeView extends Component<Props> {
	isDefaultRender: boolean;
	isAnyAppDisplayed: boolean;
	categoryChangedBeforeAllFFCDataWasFetched: boolean;
	helpPopoverTimeout: ?TimeoutID;
	isAnyAppRenderEventSent: boolean;

	constructor(props: Props) {
		super(props);
		this.helpPopoverTimeout = null;
		/* Needed for us to decide if we can show the 'View all apps' affordance */
		this.isAnyAppDisplayed = false;
		/*
		this.isDefaultRender
		- Needed for analytics
		- Is set to true on first time render and then set to false as soon as someone changes the filter
		*/
		this.isDefaultRender = false;
		/* Tells if the render app event has been sent for any app so far */
		this.isAnyAppRenderEventSent = false;
	}

	componentDidMount() {
		this.isDefaultRender = true;
		if (this.props.enableHelpTimeouts) {
			this.helpPopoverTimeout = setTimeout(() => {
				this.openGetHelpPopover();
				this.helpPopoverTimeout = setTimeout(() => this.openAutoDownloadPopover(), AUTO_DOWNLOAD_TIMEOUT);
			}, HELP_TIMEOUT);
			CSSUtil.addEventListenerToElem(window, 'click', this.clearInactivityTimeouts);
			CSSUtil.addEventListenerToElem(window, 'keydown', this.clearInactivityTimeouts);
			this.props.clearHelpTimeouts();
		}
		this.handleAnalyticsEvent(ANALYTICS.PAGE_RENDER);
		this.updateHelpPopoverConfig(undefined, true);
		this.sendAppRenderEvents(); // For the case when someone presses back button from the download page and componenet remounts
	}

	updateHelpPopoverConfig(heading: ?string, updateCompatibilityTag?: boolean) {
		const apps = [];
		if (updateCompatibilityTag) {
			this.getAppListToDisplay().forEach((app) => {
				if (
					this.isCardFFCDataAvailable(app.sapCode)
					&& (AppsUtil.getFFCProductConfigBySapCode(app.sapCode) || AppPickerUtil.isNujM3PhotoPlanTestExperience())
					&& !AppsUtil.disableProduct(app.sapCode)) {
					apps.push(app.sapCode);
				}
			});
		}
		const updatingList = {
			listId: LIST_IDS.FAQ_AND_HELP,
			heading,
			pageType: [TAGS.PAGE.APP_PICKER],
			installWorkflowTags: HelpPopoverUtil.getInstallWorkflowTag(this.props.hasThor),
			compatibilityTags: HelpPopoverUtil.getCompatibilityTags(apps)
		};
		HelpPopoverUtil.updateHelpPopoverConfig(undefined, [updatingList]);
	}

	componentDidUpdate(prevProps: Object) {
		const didCategoryChange = prevProps.selectedCategoryId !== this.props.selectedCategoryId;

		// update get get help config
		this.updateHelpPopoverConfig(undefined, true);

		if (didCategoryChange) {
			this.handleAnalyticsEvent(ANALYTICS.PAGE_RENDER);
		}

		// Send app render events
		const {props: {sapCodeDataFetchStatus: propsFetchStatus}} = this;
		const {sapCodeDataFetchStatus: prevPropsFetchStatus} = prevProps;
		const propsFetchStatusLen = propsFetchStatus ? propsFetchStatus.length : 0;
		const prevPropsFetchStatusLen = prevPropsFetchStatus ? prevPropsFetchStatus.length : 0;
		const allSapCodesToFetchLen = AppPickerUtil.getAllCategoriesSapCodes().length;

		if (propsFetchStatusLen > 0) {
			if (propsFetchStatusLen < allSapCodesToFetchLen) {
				// First set of FFC Data has been fetched, second set is still pending
				if (prevPropsFetchStatusLen !== propsFetchStatusLen) {
					// First set of FFC Data was just received
					this.sendAppRenderEvents();
					if (!this.isDefaultRender) {
						/*
							If category was already changed,
							App render event for only those apps will go which were also present in the default category
						*/
						this.categoryChangedBeforeAllFFCDataWasFetched = true;
					}
				}
				else if (didCategoryChange) {
					// Category changed
					// App render event for only those apps will go which were also present in the default category
					this.sendAppRenderEvents();
					this.categoryChangedBeforeAllFFCDataWasFetched = true;
				}
			}
			else {
				// All of the FFC Data has been fetched
				if (prevPropsFetchStatusLen !== propsFetchStatusLen) {
					// Second set of FFC Data was just received
					if (this.categoryChangedBeforeAllFFCDataWasFetched) {
						/*
							If the category was changed already before the second set of FFC data was received,
							then send the events for the apps whose event must not have gone previously, i.e.,
							for the non-default category apps
						*/
						const defaultCategorySapCodes = AppPickerUtil.getDefaultCategorySapCodes();
						const nonDefaultCategoryApps = this.getAppListToDisplay().filter(app => !defaultCategorySapCodes.includes(app.sapCode));
						this.sendAppRenderEvents(nonDefaultCategoryApps);
					}
				}
				else if (!this.isAnyAppRenderEventSent) {
					/*
						Also, if all the FFC data was fetched before homeView mounted
						(this case may occue in case of IncompatibleOSView being rendered before HomeView),
						then send the render event of apps currently visible
					*/
					this.sendAppRenderEvents();
				}
				else if (didCategoryChange) {
					this.sendAppRenderEvents();
				}
			}
		}
	}

	sendAppRenderEvents(inputApps: ?Array<Object>) {
		const apps = inputApps ? inputApps : this.getAppListToDisplay();
		apps.forEach((app) => {
			if (this.isCardFFCDataAvailable(app.sapCode)) {
				const ffcProductData = AppsUtil.getFFCProductConfigBySapCode(app.sapCode);
				if (ffcProductData) {
					const sapCode = app.sapCode;
					const appName = ffcProductData.displayName;
					this.handleAnalyticsEvent(ANALYTICS.APP_RENDER, {sapCode, appName});
					this.isAnyAppRenderEventSent = true;
				}
			}
		});
	}

	openAutoDownloadPopover() {
		HelpPopoverUtil.openAutoDownloadPopover();
	}

	openGetHelpPopover() {
		const heading = this.props.intl.formatMessage({id: GET_HELP_STRINGS.INACTIVITY_FAQ_HEADING});
		this.updateHelpPopoverConfig(heading, true);
		HelpPopoverUtil.openGetHelpPopover(GET_HELP_OPEN_SOURCES.INACTIVITY);
	}

	componentWillUnmount() {
		IncompatibleDialogUtil.hideAppPickerIncompatibleDialog();
		this.clearInactivityTimeouts();
	}

	getViewType() {
		if (this.isDefaultRender) {
			return 'first launch';
		}
		return 'filter';
	}

	handleAnalyticsEvent = (eventType: string, params: AnalyticsParam = {}) => {
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		let eventObj: AnalyticObjectInterface = {
			event: {}
		};
		const {
			sapCode = '',
			appName,
			actionId,
			ctaType = '',
			guid = ''
		} = params;

		switch (eventType) {
			case ANALYTICS.PAGE_RENDER:
				eventObj = {
					event: {
						type: 'render',
						subtype: 'page'
					},
					ui: {
						filter: this.props.selectedCategoryId,
						view_type: this.getViewType() // eslint-disable-line camelcase
					}
				};
				break;
			case INSTALL_CCD_OS_TOAST.RENDER.ID:
				eventObj = {
					event: {
						type: INSTALL_CCD_OS_TOAST.RENDER.TYPE,
						subtype: INSTALL_CCD_OS_TOAST.RENDER.SUBTYPE,
						value: INSTALL_CCD_OS_TOAST.RENDER.VALUE
					},
					ui: {
						view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
					}
				};
				break;
			case INSTALL_CCD_OS_TOAST.CTA_CLICK.ID:
				eventObj = {
					event: {
						type: INSTALL_CCD_OS_TOAST.CTA_CLICK.TYPE,
						subtype: INSTALL_CCD_OS_TOAST.CTA_CLICK.SUBTYPE,
						value: INSTALL_CCD_OS_TOAST.CTA_CLICK.VALUE,
					},
					ui: {
						view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
					}
				};
				break;
			case ANALYTICS.APP_RENDER: {
				eventObj = {
					event: {
						type: 'render',
						subtype: 'app'
					},
					content: {
						id: sapCode,
						name: appName,
						type: analytics.getCompatibilityString(
							CompatibilityUtil.isAppSupported(sapCode),
							CompatibilityUtil.isAnyOlderVersionSupported(sapCode)
						),
						version: CompatibilityUtil.getSupportedAppVersion(sapCode),
						category: 'App Card'
					},
					ui: {
						filter: this.props.selectedCategoryId,
						view_type: this.getViewType() // eslint-disable-line camelcase
					}
				};
				break;
			}
			case ANALYTICS.APP_CTA_CLICK: {
				eventObj = {
					event: {
						type: 'click',
						subtype: actionId
					},
					content: {
						id: sapCode,
						name: appName,
						type: analytics.getCompatibilityString(
							CompatibilityUtil.isAppSupported(sapCode),
							CompatibilityUtil.isAnyOlderVersionSupported(sapCode)
						),
						version: CompatibilityUtil.getSupportedAppVersion(sapCode),
						category: 'Button'
					},
					ui: {
						filter: this.props.selectedCategoryId,
						view_type: this.getViewType() // eslint-disable-line camelcase
					}
				};
				break;
			}
			case ANALYTICS.APP_CARD_CLICK: {
				eventObj = {
					event: {
						type: 'click',
						subtype: 'app-card'
					},
					content: {
						id: sapCode,
						name: appName,
						type: analytics.getCompatibilityString(
							CompatibilityUtil.isAppSupported(sapCode),
							CompatibilityUtil.isAnyOlderVersionSupported(sapCode)
						),
						version: CompatibilityUtil.getSupportedAppVersion(sapCode),
						category: 'App Card',
						action: 'disabled'
					},
					ui: {
						filter: this.props.selectedCategoryId,
						view_type: this.getViewType() // eslint-disable-line camelcase
					}
				};
				break;
			}
			case ANALYTICS.PAGE_CTA_CLICK:
				eventObj = {
					event: {
						type: 'click',
						subtype: ctaType,
						guid
					},
					content: {
						action: params && params.url
					},
					ui: {
						view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
					}
				};
				break;
			case ANALYTICS.PAGE_FILTER_CLICK:
				eventObj = {
					event: {
						type: 'click',
						subtype: 'filter'
					},
					ui: {
						filter: params && params.filterValue,
						view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
					}
				};
				break;
		}
		eventObj.event.subcategory = 'Welcome';
		analytics.genericAnalyticEvent(eventObj);
	}

	clearInactivityTimeouts = () => {
		if (this.helpPopoverTimeout) {
			clearTimeout(this.helpPopoverTimeout);
		}
		window.removeEventListener('click', this.clearInactivityTimeouts);
		window.removeEventListener('keydown', this.clearInactivityTimeouts);
	}

	onCardCTAClick = (e: Object, actionId: string, appObj: AppObject) => {
		const {sapCode, appName, platform} = appObj;
		this.handleAnalyticsEvent(ANALYTICS.APP_CTA_CLICK, {sapCode, appName, actionId});
		this.clearInactivityTimeouts();
		let plan;
		if (AppPickerUtil.isNujM3PhotoPlanTestExperience()) {
			plan = 'ccpp';
		}
		switch (actionId) {
			case ACTION_IDS.DOWNLOAD: {
				if (HelpPopoverUtil.closeAutoDownloadPopover) {
					HelpPopoverUtil.closeAutoDownloadPopover();
				}
				const url = `${AppsUtil.getInstallationModalUrl({sapCode, plan, source: AppPickerUtil.getSourceQueryParam(), platform: platform.toLowerCase()})}&isStarter=true`;
				if (AppsUtil.hasFeature('temp_cch_10855')) {
					window._uxa = window._uxa || [];
					window._uxa.push(['trackPageview', url]);
				}
				this.props.history.push(url);
				break;
			}
			case ACTION_IDS.LEARN_MORE:
				this.props.showIncompatibilityDialog(sapCode);
				break;
		}
	}

	onCardClick = (appObj: AppObject) => {
		const {sapCode, appName} = appObj;
		this.handleAnalyticsEvent(ANALYTICS.APP_CARD_CLICK, {sapCode, appName});
	}

	onBackToTopClick = () => {
		this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'back-to-top'});
		CSSUtil.animateScrollToTop(25, document.getElementById('all-apps-app-picker'), 1);
	}

	onViewYourPlanButtonClick = () => {
		this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'view-plan'});
	}

	onCategorySelect = (category: string) => {
		this.props.onCategorySelect(category);
		/*
		Once a user clicks on category selector this is no longer a default render or view all apps clicked in analytics
		*/
		this.isDefaultRender = false;
		if (this.props.selectedCategoryId !== category) {
			this.handleAnalyticsEvent(ANALYTICS.PAGE_FILTER_CLICK, {filterValue: category});
		}
	}

	getAppListToDisplay() {
		if (this.isAllAppsWeightedAppPickerCategory()) {
			const appWithAffinity = AppPickerUtil.getUserAffinitySapcode();
			const appWithAffinityData = AppPickerUtil.getAppData(appWithAffinity);
			const appList = [{sapCode: appWithAffinity, ...appWithAffinityData}];
			appWithAffinityData.moreApps && appWithAffinityData.moreApps.forEach((sapCode) => {
				appList.push({sapCode, ...AppPickerUtil.getAppData(sapCode)});
			});
			return appList;
		}
		return AppPickerUtil.getTaskObjectFromTaskId(this.props.selectedTaskId, this.props.selectedCategoryId).appList;
	}

	isCardFFCDataAvailable(sapCode: string) {
		const {props: {sapCodeDataFetchStatus}} = this;
		return sapCodeDataFetchStatus
			&& sapCodeDataFetchStatus.find(obj => obj.sapCode === sapCode && obj.status === true)
			&& AppsUtil.isAppLineageMapInit;
	}

	isAllAppsWeightedAppPickerCategory() {
		return AppPickerUtil.showRecommenededInDropdown()
			&& (this.props.selectedCategoryId === AppPickerUtil.getDefaultCategoryId());
	}

	isWeightedAppPicker() {
		return AppPickerUtil.isNujM3PhotoPlanTestExperience() || this.isAllAppsWeightedAppPickerCategory();
	}

	downloadCreativeCloud = (isViaInstallCCDToast: ?boolean) => {
		const sapcode = (isViaInstallCCDToast || this.props.hasThor) ? 'KCCC' : AppPickerUtil.getDefaultDownloadProduct();
		const productConfig = AppsUtil.getFFCProductConfigBySapCode(sapcode);
		let queryParam = '&isStarter=true';
		if (AppPickerUtil.getDefaultDownloadProduct() !== 'KCCC' && !this.props.hasThor) {
			/**
			 * Kaizen workflow and some product out of the products listed in the plan are compatible (other than KCCC),
			 * then download that product via CCD
			 */
			queryParam = `${queryParam}&${DOWNLOAD_VIA_CCD_QUERY_PARAM.QUERY_PARAM}=${DOWNLOAD_VIA_CCD_QUERY_PARAM.QUERY_VALUE}`;
		}
		if (isViaInstallCCDToast) {
			this.handleAnalyticsEvent(INSTALL_CCD_OS_TOAST.CTA_CLICK.ID);
		}
		else {
			this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'install-ccd'});
		}
		if (productConfig) {
			const url = `${AppsUtil.getInstallationModalUrl({
				sapCode: sapcode,
				source: AppPickerUtil.getSourceQueryParam(),
				platform: AppsUtil.getProductPlatform(productConfig)
			})}${queryParam}`;
			if (AppsUtil.hasFeature('temp_cch_10855')) {
				window._uxa = window._uxa || [];
				window._uxa.push(['trackPageview', url]);
			}
			this.props.history.push(url);
		}
	}

	redirectToCreativeCloudWeb = () => {
		const guid = uuid.v4();
		const url = AppsUtil.openExternalLink(window.location.origin, undefined, guid, undefined, undefined, '_self');
		this.handleAnalyticsEvent(ANALYTICS.PAGE_CTA_CLICK, {ctaType: 'already-have-apps', url, guid});
	}

	getDnldCCDLabel() {
		return this.props.hasThor
			? 'apps.allAppsAppPicker.footer.title.thor'
			: 'apps.allAppsAppPicker.footer.title.kaizen';
	}

	getHeadingStringIds() {
		if (AppPickerUtil.isNUJDeprecatedOS()) {
			return {
				heading: 'apps.allAppsAppPicker.welcomeMessage.heading.incompatibleOs',
				subHeading: 'apps.allAppsAppPicker.welcomeMessage.subHeading.incompatibleOs'
			};
		}
		if (AppPickerUtil.isNujM3PhotoPlanTestExperience()) {
			return {
				heading: 'apps.revampedPhotoPlanAppPicker.welcomeMessage.heading',
				subHeading: 'apps.revampedPhotoPlanAppPicker.welcomeMessage.subHeading'
			};
		}
		return {
			heading: 'apps.allAppsAppPicker.welcomeMessage.heading',
			subHeading: 'apps.allAppsAppPicker.welcomeMessage.subHeading'
		};
	}

	renderIncompatibleOSToast() {
		const {hasThor, onIncompatibleOSToastDismiss} = this.props;
		const variant = MESSAGE_BOX_VARIANT.INCOMPATIBLE_OS_TOAST;
		return (<MessageBox
			variant={variant}
			hasThor={hasThor}
			showActionCTA={AppsUtil.getFFCProductConfigBySapCode('KCCC')}
			onAction={() => {this.downloadCreativeCloud(true);}}
			onClose={onIncompatibleOSToastDismiss}
			intl={this.props.intl}
			key={variant}
			handleAnalyticsEvent = {this.handleAnalyticsEvent}
		/>);
	}

	renderHeadingSection = () => (
		<div className={HomeViewScss.headingSection} key='home-view-header'>
			<div data-cs-mask='' className={HomeViewScss.heading}>
				{this.props.intl.formatMessage(
					{id: this.getHeadingStringIds().heading},
					{username: this.props.imsData.name}
				)}
			</div>
			<div className={HomeViewScss.subHeading}>
				<span>{this.props.intl.formatMessage({id: this.getHeadingStringIds().subHeading})}</span>
			</div>
		</div>
	);

	getCardElem(app: Object, isRevampedCard?: boolean, isFirstCard?: boolean) {
		const ffcProductData = AppsUtil.getFFCProductConfigBySapCode(app.sapCode);
		const appCardData = AppPickerUtil.getConsolidatedAppCardData(
			ffcProductData,
			app,
			this.props.locale
		);
		const appObj: AppObject = {
			sapCode: app.sapCode,
			appName: appCardData.title,
			platform: (ffcProductData && AppsUtil.getProductPlatform(ffcProductData)) || 'desktop'
		};
		const appCardProps = {
			className: 'appPickerAppCard',
			title: appCardData.title,
			imageSrc: appCardData.imageSrc,
			supportedPlatform: appCardData.supportedPlatform,
			subtitle: isRevampedCard
				? appCardData.productDetailsFromCVS && appCardData.productDetailsFromCVS.map(detail => ({
					description: AppsUtil.hasFeature('temp_cch_7467') ? (detail && detail.appDetail && detail.appDetail.html) : (detail && detail.description),
					showIcon: !!isFirstCard,
					iconType: detail && detail.descriptionThumbnail
				}))
				: appCardData.ffcShortDescription,
			bannerPlacement: 'top',
			showSupportedPlatforms: !(isRevampedCard && isFirstCard),
			bannerSrc: (isRevampedCard && isFirstCard) ? appCardData.wideBannerSrc : appCardData.bannerSrc,
			actionList: AppPickerUtil.getActionList(app.sapCode, appCardData.title),
			onAction: (e, actionId) => {
				this.onCardCTAClick(e, actionId, appObj);
			},
			onCardSelect: () => this.onCardClick(appObj)
		};
		if (isRevampedCard) {
			if (isFirstCard) {
				appCardProps.bannerPlacement = 'right';
				appCardProps.actionList[0].variant = 'cta';
				if (app.sapCode === 'LRCC') {
					appCardProps.flyerText = this.props.intl.formatMessage({id: 'apps.revampedPhotoPlanAppPicker.card.photo-plan.flyerText'});
				}
				if (this.isAllAppsWeightedAppPickerCategory()) {
					appCardProps.flyerText = this.props.intl.formatMessage({id: 'apps.revampedPhotoPlanAppPicker.card.all-apps-plan.flyerText'});
				}
			}
			else {
				appCardProps.bannerPlacement = 'hide';
			}
		}

		return (
			<AppCard
				key={`app-card-${app.sapCode}`}
				{...appCardProps} />
		);
	}

	renderCardList = () => {
		const cardList = [];
		this.getAppListToDisplay().forEach((app) => {
			if (!this.isCardFFCDataAvailable(app.sapCode)) {
				cardList.push(
					<div className={HomeViewScss.cardSkeleton} key={`app-card-${app.sapCode}`}>
						<CardSkeleton />
					</div>
				);
			}
			else {
				const ffcProductData = AppsUtil.getFFCProductConfigBySapCode(app.sapCode);
				if (ffcProductData && !AppsUtil.disableProduct(app.sapCode)) {
					cardList.push(
						<div className={HomeViewScss.card} key={`app-card-${app.sapCode}`}>
							{this.getCardElem(app)}
						</div>
					);
					this.isAnyAppDisplayed = true;
				}
			}
		});
		return (
			<div className={HomeViewScss.cardList}>
				{cardList}
			</div>
		);
	}

	renderWeightedCardList = () => {
		const cardList = [];
		const appListToDisplay = this.getAppListToDisplay();
		if (appListToDisplay.some(app => !this.isCardFFCDataAvailable(app.sapCode))) {
			// As long as we don't get data for each of the app, we show skeleton. Because without full data we won't know the ordering
			appListToDisplay.forEach((app, index) => {
				cardList.push(
					<div className={`${index === 0 ? HomeViewScss.firstCardSkeleton : HomeViewScss.cardSkeleton}${this.isAllAppsWeightedAppPickerCategory() ? ` ${HomeViewScss.threeCardList}` : ''}`} key={`app-card-${app.sapCode}`}>
						<CardSkeleton />
					</div>
				);
			});
			return (
				<div className={`${HomeViewScss.cardList} ${HomeViewScss.revampedCardList}`}>
					{cardList}
				</div>
			);
		}
		const firstCompatibleProduct = appListToDisplay.find(
			app => CompatibilityUtil.isAppSupported(app.sapCode) || CompatibilityUtil.isAnyOlderVersionSupported(app.sapCode)
		) || appListToDisplay[0];
		const otherProducts = appListToDisplay.filter(
			app => app.sapCode !== firstCompatibleProduct.sapCode
		);
		otherProducts.forEach((app) => {
			if (!AppsUtil.disableProduct(app.sapCode)) {
				cardList.push(
					<div className={`${HomeViewScss.card}${this.isAllAppsWeightedAppPickerCategory() ? ` ${HomeViewScss.threeCardList}` : ''}`} key={`app-card-${app.sapCode}`}>
						{this.getCardElem(app, true)}
					</div>
				);
				this.isAnyAppDisplayed = true;
			}
		});
		const firstCompatibleProductCard = (
			<div className={HomeViewScss.firstCard} key={`app-card-${firstCompatibleProduct.sapCode}`}>
				{this.getCardElem(firstCompatibleProduct, true, true)}
			</div>
		);
		return (
			<div className={`${HomeViewScss.cardList} ${HomeViewScss.revampedCardList}`}>
				<div className={HomeViewScss.cardListTitle}>
					{((firstCompatibleProduct.sapCode === 'LRCC') || this.isAllAppsWeightedAppPickerCategory())
						? this.props.intl.formatMessage({id: 'apps.revampedPhotoPlanAppPicker.topCardTitle.recommended'})
						: this.props.intl.formatMessage({id: 'apps.revampedPhotoPlanAppPicker.topCardTitle.compatible'})}
				</div>
				{firstCompatibleProductCard}
				{(cardList.length > 0) && <div className={HomeViewScss.cardListTitle}>
					{this.props.intl.formatMessage({id: 'apps.revampedPhotoPlanAppPicker.bottomCardsTitle'})}
				</div>}
				{cardList}
			</div>
		);
	}

	renderViewYourPlanAffordance() {
		const planName = AppPickerUtil.getViewYourPlanModalContent().displayPlanName
			? AppPickerUtil.getViewYourPlanModalContent().displayPlanName
			: undefined;
		const viewPlanLabel = planName
			? this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.viewYourPlanWithPlanName'}, {planName})
			: this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.viewYourPlan'});
		return (
			<div className={HomeViewScss.viewYourPlan}>
				<ModalTrigger>
					<Button
						quiet
						onClick={this.onViewYourPlanButtonClick}
						variant='secondary'
						className={HomeViewScss.viewYourPlanButton}
						label={viewPlanLabel}
						aria-label={viewPlanLabel}
					/>
					<Dialog
						className={PlanModalContentScss.viewYourPlanModal}
						confirmLabel={this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.viewYourPlan.modal.done'})}
						role='dialog'
						onClose={() => {}}
						title={AppPickerUtil.getViewYourPlanModalContent().title}
					>
						<PlanModalContent
							formatMessage={this.props.intl.formatMessage}
							source={DOWNLOAD_CONTROLLER_USER_SOURCE.APP_PICKER_DOWNLOAD_PAGE}
						/>
					</Dialog>
				</ModalTrigger>
			</div>
		);
	}

	renderAffordance() {
		if (AppPickerUtil.showAllDesktopAppsInDropdown()) {
			let affordance;
			if (this.props.selectedCategoryId === ALL_APPS) {
				affordance = (
					<Button
						quiet
						variant='secondary'
						onClick={this.onBackToTopClick}
						label={this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.affordance.back-to-top'})}
					/>
				);
			}
			return (
				<div className={HomeViewScss.affordance}>
					{affordance}
				</div>
			);
		}
		return null;
	}

	renderBodySection = () => (
		<div className={HomeViewScss.bodySection} key='home-view-body'>
			<div className={HomeViewScss.intentDropdown}>
				{AppPickerUtil.isAllAppsPlan() && <IntentDropdown
					intl={this.props.intl}
					categoryTaskData={AppPickerUtil.getContentfulCategoriesList()}
					onCategorySelect={this.onCategorySelect}
					onTaskSelect={this.props.onTaskSelect}
					selectedCategoryId={this.props.selectedCategoryId}
					selectedTaskId={this.props.selectedTaskId}
					renderOnlyCategories={this.props.renderOnlyCategories}
				/>}
				{AppPickerUtil.getViewYourPlanModalContent() && this.renderViewYourPlanAffordance()}
			</div>
			{this.isWeightedAppPicker() ? this.renderWeightedCardList() : this.renderCardList()}
			{this.isAnyAppDisplayed && this.renderAffordance()}
		</div>
	);

	renderFooterSection = () => (
		<div className={HomeViewScss.footerSection} key='home-view-footer'>
			<Button
				quiet
				variant='secondary'
				disabled={!AppPickerUtil.isDefaultDownloadProductFFCDataAvailable()}
				onClick={() => {this.downloadCreativeCloud();}}
				className={HomeViewScss.dnld_ccd_button}
				label={this.props.intl.formatMessage({id: this.getDnldCCDLabel()})}
				aria-label={this.props.intl.formatMessage({id: this.getDnldCCDLabel()})}
			/>
			{<Button
				quiet
				variant='secondary'
				onClick={this.redirectToCreativeCloudWeb}
				className={HomeViewScss.redirect_ccweb_button}
				label={this.props.intl.formatMessage({id: 'apps.allAppsAppPicker.footer.subTitle'})}
			/>}
		</div>
	);

	render() {
		return (
			<div id='all-apps-app-picker' className={HomeViewScss.appsPickerContainer}>
				{this.renderHeadingSection()}
				{this.renderBodySection()}
				{this.props.showIncompatibleOSToast && this.renderIncompatibleOSToast()}
			</div>
		);
	}
}
